import { useEffect } from "react";

import ReactDOM from "react-dom";
import { BrowserRouter, Route, Switch, Redirect } from "react-router-dom";
import { Provider } from "react-redux";
import { QueryClientProvider } from "@tanstack/react-query";

import { queryClient } from "lib/queryClient";
import CssBaseline from "@material-ui/core/CssBaseline";
import { ThemeProvider } from "@material-ui/core/styles";

import { TourProvider, useTour } from "@reactour/tour";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";

import "react-perfect-scrollbar/dist/css/styles.css";
import "@fullcalendar/common/main.min.css";
import "@fullcalendar/daygrid/main.min.css";
import "quill/dist/quill.core.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
// plugins styles downloaded
import "assets/plugins/nucleo/css/nucleo.css";
// core styles
import "assets/scss/argon-dashboard-pro-material-ui.scss?v1.0.0";
import "assets/css/index.css";
import theme from "assets/theme/theme.js";
import AdminLayout from "layouts/admin/page";
import AuthLayout from "layouts/auth.js";
import SnackbarProvider from "components/Alerts/Snackbar/SnackbarProvider";
import { initGA, trackingPageGA } from "./lib/analytics";
import store from "./redux/store";
import RedirectPage from "views/auth/RedirectPage";
import VitrineVirtualClientes from "views/vitrine-virtual/page";
import NFe from "views/nfe/page";
import VitrineEmpresaPage from "views/vitrine-b2b/vitrine-empresa/page";
import ExclusaoConta from "views/exclusao-conta/page";
import NotasAtualizacaoPublica from "views/notas-atualizacao-publica/page";

const b2bTestUrl = "https://hml.karsten.digital/";

const getBasename = () => {
  const publicUrl = process.env.PUBLIC_URL;
  if (publicUrl) {
    return publicUrl.replace("@@homepage@@", "").replace(b2bTestUrl, "");
  }
};

const getInitialPath = () => {
  const publicUrl = process.env.PUBLIC_URL;
  const windowUrl = window.location.href;

  if (windowUrl.includes("index.html?")) {
    const baseUrl = windowUrl
      .replace(publicUrl, "")
      .replace(b2bTestUrl, "")
      .replace("http://localhost:3000/", "")
      .replace("@@homepage@@", "")
      .replace("index.html?", "");

    return baseUrl;
  }
};

const divContainerStyle = {
  display: "flex",
  flexDirection: "column",
  width: "20rem",
};
const titleStyle = {
  fontWeight: "800",
  color: "#E7848D",
  fontSize: "1.5rem",
  textShadow: "0 0 1px red",
  textDecoration: "underline",
};
const textStyle = {
  fontWeight: "700",
  fontSize: "1.25rem",
  color: "#525F7F",
  marginTop: "1rem",
};
const divButtonStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  width: "100%",
};
const divTextStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "92%",
};
const buttonNextStyle = {
  border: "none",
  backgroundColor: "transparent",
  marginRight: "1rem",
};
const buttonCloseStyle = {
  border: "none",
  backgroundColor: "transparent",
  fontWeight: "700",
  fontSize: "1rem",
  color: "#525F7F",
};

const App = () => {
  const { setIsOpen } = useTour();
  const basename = getBasename();
  const initialPath = getInitialPath();

  const disableBody = (target) => disableBodyScroll(target);
  const enableBody = (target) => enableBodyScroll(target);

  useEffect(() => {
    initGA(); // Initialize google analytics
    trackingPageGA("/"); // para testar que um usuario abriu a aplicação
  }, []);

  const tourConfig = [
    {
      selector: '[data-tut="reactour__step1"]',
      disableActions: true,
      content: function DemoHelperComponent({ setCurrentStep }) {
        let title = "";
        let text = "";
        let storage = "";

        if (window.location.href?.includes("lojista-360")) {
          title = "Lojista 360";
          text = "Para fazer a pesquisa você pode escolher um Grupo.";
          storage = "lojistaStep1";
        } else if (
          window.location.href?.includes("relatorio-acompanhamento-titulos")
        ) {
          title = "Relatório de Acompanhamento de Títulos";
          text = 'Para realizar a busca, é necessário escolher uma "Marca".';
          storage = "relatorioStep1";
        } else if (window.location.href?.includes("home")) {
          title = "Favoritar tela";
          text =
            "Clicando no campo de busca aparecerão as opções para adicionar aos favoritos.";
          storage = "homeStep1";
        }
        return (
          <div style={divContainerStyle}>
            <h3 style={titleStyle}>{title}</h3>
            <div role="presentation" style={divTextStyle}>
              <p style={textStyle}>{text}</p>
            </div>
            <div role="presentation" style={divButtonStyle}>
              <button
                style={buttonNextStyle}
                onClick={() => {
                  setCurrentStep(1);
                  localStorage.setItem(storage, storage);
                }}
              >
                <i
                  className="fa fa-arrow-circle-right fa-2x"
                  style={{ color: "#E7848B" }}
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
        );
      },
    },
    {
      selector: '[data-tut="reactour__step2"]',
      disableActions: true,
      content: function DemoHelperComponent({ setCurrentStep }) {
        let title = "";
        let text = "";
        let storage = "";

        if (window.location.href?.includes("lojista-360")) {
          title = "Lojista 360";
          text = "Escolher uma Razão Social / Loja.";
        } else if (
          window.location.href?.includes("relatorio-acompanhamento-titulos")
        ) {
          title = "Relatório de Acompanhamento de Títulos";
          text = 'Escolher um "Representante".';
        } else if (window.location.href?.includes("home")) {
          title = "Favoritar tela";
          text = "Após, seus favoritos serão listados aqui.";
          storage = "homeStep2";
        }

        return (
          <div style={divContainerStyle}>
            <h3 style={titleStyle}>{title}</h3>
            <div role="presentation" style={divTextStyle}>
              <p style={textStyle}>{text}</p>
            </div>
            <div role="presentation" style={divButtonStyle}>
              {storage !== "" ? (
                <button
                  style={buttonCloseStyle}
                  onClick={() => {
                    localStorage.setItem(storage, storage);
                    localStorage.removeItem("openMenu");
                    setCurrentStep(2);
                    setTimeout(() => {
                      setCurrentStep(0);
                    }, 500);
                  }}
                >
                  Fechar
                </button>
              ) : (
                <button
                  style={buttonNextStyle}
                  onClick={() => {
                    setCurrentStep(2);
                  }}
                >
                  <i
                    className="fa fa-arrow-circle-right fa-2x"
                    style={{ color: "#E7848B" }}
                    aria-hidden="true"
                  />
                </button>
              )}
            </div>
          </div>
        );
      },
    },
    {
      selector: '[data-tut="reactour__step3"]',
      disableActions: true,
      content: function DemoHelperComponent({ setCurrentStep }) {
        let title = "";
        let text = "";

        if (window.location.href?.includes("lojista-360")) {
          title = "Lojista 360";
          text = "Ou então inserir um CNPJ.";
        } else if (
          window.location.href?.includes("relatorio-acompanhamento-titulos")
        ) {
          title = "Relatório de Acompanhamento de Títulos";
          text = 'E também escolher uma "Situação do Título".';
        }
        return (
          <div style={divContainerStyle}>
            <h3 style={titleStyle}>{title}</h3>
            <p style={textStyle}>{text}</p>
            <div role="presentation" style={divButtonStyle}>
              <button
                style={buttonNextStyle}
                onClick={() => {
                  setCurrentStep(3);
                }}
              >
                <i
                  className="fa fa-arrow-circle-right fa-2x"
                  style={{ color: "#E7848B" }}
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
        );
      },
    },
    {
      selector: '[data-tut="reactour__step4"]',
      disableActions: true,
      content: function DemoHelperComponent({ setCurrentStep }) {
        let title = "";
        let text = "";
        let storage = "";

        if (window.location.href?.includes("lojista-360")) {
          title = "Lojista 360";
          text =
            "Você pode ainda clicar aqui e fazer a busca com outros filtros.";
          storage = "lojistaStep4";
        } else if (
          window.location.href?.includes("relatorio-acompanhamento-titulos")
        ) {
          title = "Relatório de Acompanhamento de Títulos";
          text = 'Após clique no botão "Pesquisar" para fazer a busca.';
          storage = "relatorioStep4";
        }
        return (
          <div style={divContainerStyle}>
            <h3 style={titleStyle}>{title}</h3>
            <div role="presentation" style={divTextStyle}>
              <p style={textStyle}>{text}</p>
            </div>
            <div role="presentation" style={divButtonStyle}>
              <button
                style={buttonCloseStyle}
                onClick={() => {
                  setCurrentStep(0);
                  localStorage.setItem(storage, storage);
                }}
              >
                Fechar
              </button>
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider>
        <TourProvider
          showCloseButton={false}
          disableInteraction={true}
          showPrevNextButtons={false}
          showNavigation={false}
          steps={tourConfig}
          afterOpen={disableBody}
          beforeClose={enableBody}
          badgeContent={({ totalSteps, currentStep }) => {
            const verifyFavoriteTour = localStorage.getItem("openMenu");
            if (!verifyFavoriteTour) {
              return `${currentStep + 1}/${totalSteps}`;
            } else {
              return `${currentStep + 1}/2`;
            }
          }}
          styles={{
            maskArea: (base) => ({ ...base, rx: 25 }),
            maskWrapper: (base) => ({ ...base, color: "black" }),
            badge: (base) => ({
              ...base,
              left: "auto",
              right: "-0.8125rem",
              background: "#E7848B",
            }),
            popover: (base) => ({
              ...base,
              backgroundColor: "#F5F8FB",
              borderRadius: 10,
              paddingRight: "20rem",
            }),
          }}
          scrollSmooth
        >
          <BrowserRouter basename={basename}>
            <Switch>
              <Route
                exact
                path="/r/:url"
                render={(props) => <RedirectPage {...props} />}
              />
              <Route
                path="/auth"
                render={(props) => <AuthLayout {...props} />}
              />
              <Route
                path="/admin"
                render={(props) => <AdminLayout {...props} />}
              />
              <Route
                path="/home"
                render={(props) => <AdminLayout {...props} />}
              />
              <Route
                path="/vitrine-virtual-cliente"
                render={(props) => <VitrineVirtualClientes {...props} />}
              />
              <Route
                path="/vitrine-virtual-b2b"
                render={(props) => <VitrineEmpresaPage {...props} />}
              />
              <Route path="/nfe" render={(props) => <NFe {...props} />} />
              <Route
                path="/exclusao-de-conta"
                render={(props) => <ExclusaoConta {...props} />}
              />
              <Route
                path="/notas-de-atualizacao-knt"
                render={(props) => <NotasAtualizacaoPublica {...props} />}
              />
              <Redirect from="*" to={initialPath || "/auth"} />
            </Switch>
          </BrowserRouter>
        </TourProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
};

ReactDOM.render(
  <Provider store={store}>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </Provider>,
  document.querySelector("#root"),
);
